import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from "@/components/ui/button";
import { Menu, X, ChevronDown, User, Phone, LogOut, Settings, ShoppingBag, Shield, Home, Users } from 'lucide-react';
import { cn } from "@/lib/utils";
import PhonePopover from '../PhonePopover';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from "@/components/ui/navigation-menu";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const accountMenuRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user, logout, loading: authLoading } = useAuth();
  const isHome = location.pathname === '/';
  const loginTo = location.pathname === '/book'
    ? `/login?returnTo=${encodeURIComponent(`/book${location.search ? `${location.search}&` : '?'}resume=1`)}` : '/login';
  const isAdmin = user?.roles?.includes('admin');
  
  // Pages that should have transparent navbar behavior (like homepage)
  const transparentNavPages = ['/', '/services/aerial-photos-video', '/services/photography', '/services/real-estate-photography', '/services/headshots-portraits', '/contact', '/services/virtual-twilight', '/services/ai-upscaling-restoration', '/services/photo-restoration', '/services/photo-restoration-enhancement', '/services/digital-persona-development', '/services/private-sessions'];
  const hasTransparentNav = transparentNavPages.includes(location.pathname);
  
  // State for scroll position logic
  const [isTop, setIsTop] = useState(true);
  const [isOverlay, setIsOverlay] = useState(true);

  // Handle scroll effect based on hero section
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      
      // Check if at top (first 50px)
      setIsTop(scrollY < 50);

      const hero = document.querySelector('.hero-main');
      if (!hero) {
        // If no hero, default to solid behavior if scrolled
        setIsOverlay(scrollY <= 50);
        return;
      }
      
      const headerHeight = 72; // Fixed header height
      const heroBottom = hero.getBoundingClientRect().bottom;
      
      // If the bottom of the hero is still below the header, we are overlaying
      if (heroBottom > headerHeight) {
        setIsOverlay(true);
      } else {
        setIsOverlay(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleScroll);
    
    // Initial check
    handleScroll();
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [location.pathname]);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
    setExpandedCategory(null);
    setAccountMenuOpen(false);
  }, [location.pathname]);

  // Handle outside click and Escape key for account dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(event.target)) {
        setAccountMenuOpen(false);
      }
    };

    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        setAccountMenuOpen(false);
      }
    };

    if (accountMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [accountMenuOpen]);

  // Handle logout
  const handleLogout = async () => {
    setAccountMenuOpen(false);
    await logout();
    navigate('/');
  };

  // Determine Header State
  const headerClass = cn(
    "w-full z-[999] transition-all duration-300 ease-in-out border-none shadow-none",
    hasTransparentNav
      ? (isTop
          ? "fixed top-0 bg-transparent" // Fully transparent at top
          : "fixed top-0 bg-[#050816]/95 backdrop-blur-md") // Solid dark after scroll
      : "sticky top-0 bg-[#050816]" // Solid dark for other pages
  );

  // Text Styling - Softer off-white with refined gold hover
  const textColorClass = "text-[#E8E4DE] hover:text-[#C6A14A] transition-colors duration-200";

  // Logo Logic - Always White (Inverted) for contrast on dark/transparent
  const logoFilter = "brightness(0) invert(1)";

  // Updated service groups structure - Only showing pages with actual content
  const serviceGroups = [
    {
      title: "Real Estate Media",
      items: [
        { name: "Real Estate Photography", href: "/services/photography" },
        { name: "Aerial Photos & Video", href: "/services/aerial-photos-video" },
        { name: "Vacant Land Drone Photos", href: "/book?type=land" },
        { name: "Virtual Twilight", href: "/services/virtual-twilight" },
        { name: "View All Real Estate Pricing", href: "/pricing/real-estate" },
      ]
    },
    {
      title: "Portraits & Photography",
      items: [
        { name: "Headshots & Portraits", href: "/services/headshots-portraits" },
        { name: "Private Sessions", href: "/services/private-sessions" },
        { name: "Pet Photography", href: "/services/pet-photography" },
        { name: "View All Portrait Pricing", href: "/pricing/portraits" },
      ]
    },
    {
      title: "Video & Brand Content",
      items: [
        { name: "View All Video Pricing", href: "/pricing/video-brand" },
      ]
    },
    {
      title: "Web, AI & Digital Systems",
      items: [
        { name: "Website & Platform Development", href: "/services/website-design" },
        { name: "AI Systems & Automation", href: "/services/ai-systems-automation" },
        { name: "AI Content & Digital Personas", href: "/services/digital-persona-development" },
        { name: "Photo Restoration & Enhancement", href: "/services/photo-restoration-enhancement", orderHref: "/order/photo-restoration" },
        { name: "Photo Scanning & Digitizing", href: "/order/photo-scanning", orderHref: "/order/photo-scanning" },
        { name: "View All Creative Pricing", href: "/pricing/creative-digital" },
      ]
    }
  ];

  // Top-level nav items
  const navItems = ['Home', 'Explore', 'Pricing', 'About', 'Contact Us'];

  const toggleMobileCategory = (title) => {
    setExpandedCategory(expandedCategory === title ? null : title);
  };

  return (
    <header className={headerClass}>
      {/* Inner Container - Widened for comfortable spacing */}
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 xl:px-8 2xl:px-16 gap-6 min-h-[72px] flex items-center justify-between">
        
        {/* Left Side: Logo + Navigation */}
        <div className="flex items-center gap-10 lg:gap-14 xl:gap-10 2xl:gap-20 min-w-0">
          {/* Logo */}
          <Link to="/" className="shrink-0 flex items-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_page-builder-81/artifacts/a7jf2g4n_RCSTUDIOS-logo-h240.png"
              alt="RC Studios" 
              className="h-6 md:h-8 w-auto object-contain"
              style={{ filter: logoFilter }}
            />
          </Link>

          {/* Navigation Menu - Editorial Text Style */}
          <div className="hidden xl:flex items-center">
            <NavigationMenu>
              <NavigationMenuList className="gap-8 xl:gap-6 2xl:gap-12">
                {navItems.map((item) => {
                  let path;
                  if (item === 'Home') path = '/';
                  else if (item === 'Contact Us') path = '/contact';
                  else if (item === 'Explore') path = '#';
                  else path = `/${item.toLowerCase().replace(' ', '-')}`;
                  
                  const isActive = location.pathname === path;
                  
                  // Explore Mega Dropdown
                  if (item === 'Explore') {
                    return (
                      <NavigationMenuItem key={item}>
                        <NavigationMenuTrigger 
                          className="font-medium text-[13px] uppercase tracking-[0.14em] 2xl:tracking-[0.2em] text-[#f3f3f3] hover:text-[#C6A14A] transition-all duration-200 px-0 data-[state=open]:text-[#C6A14A] whitespace-nowrap"
                          style={{ background: 'transparent', backgroundColor: 'transparent', boxShadow: 'none', border: 'none' }}
                        >
                          <span className="relative group">
                            {item}
                            <span className="absolute -bottom-1 left-0 w-0 h-[1px] bg-[#C6A14A] group-hover:w-full transition-all duration-300"></span>
                          </span>
                        </NavigationMenuTrigger>
                        <NavigationMenuContent className="!bg-[#0f1724]">
                          <div 
                            className="grid w-[480px] gap-0 p-10 md:w-[780px] md:grid-cols-2 lg:w-[980px] lg:grid-cols-4 rounded-2xl relative z-10"
                            style={{
                              backgroundColor: '#0f1724',
                            }}
                          >
                            {serviceGroups.map((group, groupIndex) => (
                              <div key={group.title} className={cn(
                                "space-y-5 py-3",
                                groupIndex > 0 && "md:border-l md:border-white/[0.05] md:pl-10"
                              )}>
                                <h4 className="text-[10px] font-semibold uppercase tracking-[0.3em] text-[#C6A14A] pb-3 border-b border-white/[0.05]">
                                  {group.title}
                                </h4>
                                <ul className="space-y-1">
                                  {group.items.map((service) => (
                                    <li key={service.name}>
                                      <Link
                                        to={service.href}
                                        className="group/item block py-2.5 text-[14px] font-medium text-[#f3f3f3] hover:text-[#C6A14A] transition-all duration-200 relative"
                                      >
                                        <span className="relative">
                                          {service.name}
                                          <span className="absolute bottom-0 left-0 w-0 h-[1px] bg-[#C6A14A]/50 group-hover/item:w-full transition-all duration-300"></span>
                                        </span>
                                      </Link>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            ))}
                          </div>
                        </NavigationMenuContent>
                      </NavigationMenuItem>
                    );
                  }

                  // Standard Links - Editorial Text Style with whitespace-nowrap
                  return (
                    <NavigationMenuItem key={item}>
                      <NavigationMenuLink asChild>
                        <Link 
                          to={path} 
                          className={cn(
                            "relative group font-medium text-[13px] uppercase tracking-[0.14em] 2xl:tracking-[0.2em] transition-all duration-200 px-0 whitespace-nowrap",
                            "text-[#f3f3f3] hover:text-[#C6A14A]",
                            isActive && "text-[#C6A14A]"
                          )}
                        >
                          {item}
                          {/* Hover/Active Underline */}
                          <span className={cn(
                            "absolute -bottom-1 left-0 h-[1px] bg-[#C6A14A] transition-all duration-300",
                            isActive ? "w-full" : "w-0 group-hover:w-full"
                          )}></span>
                        </Link>
                      </NavigationMenuLink>
                    </NavigationMenuItem>
                  );
                })}
              </NavigationMenuList>
            </NavigationMenu>
          </div>
        </div>

        {/* Far Right: Buttons */}
        <div className="hidden xl:flex items-center gap-4 2xl:gap-6 shrink-0">
          {/* Phone Number with Dropdown */}
          <PhonePopover variant="navbar" data-testid="navbar-phone" />
          
          {/* Book a Shoot Button - Always Visible */}
          <Button 
            asChild
            className={cn(
              "rounded-lg px-5 2xl:px-7 font-medium border-none h-10 transition-all duration-300 text-[13px] tracking-[0.1em] whitespace-nowrap",
              "bg-[#C6A14A] text-[#050816] hover:bg-[#D4AF5A]"
            )}
            data-testid="navbar-book-shoot-btn"
          >
            <Link to="/book">Book a Shoot</Link>
          </Button>
          
          {/* Account Dropdown or Sign In */}
          {isAuthenticated ? (
            <div className="relative" ref={accountMenuRef}>
              <Button
                onClick={() => setAccountMenuOpen(!accountMenuOpen)}
                variant="ghost"
                className={cn(
                  "rounded-lg px-4 font-medium h-10 transition-all duration-300 text-[13px] tracking-[0.1em]",
                  "bg-white/[0.08] text-[#E8E4DE] hover:bg-white/[0.12] hover:text-white border border-white/[0.06]",
                  accountMenuOpen && "bg-white/[0.12] text-[#C6A14A]"
                )}
                aria-expanded={accountMenuOpen}
                aria-haspopup="true"
                data-testid="navbar-account-dropdown-btn"
              >
                <User className="h-4 w-4 mr-2" />
                <ChevronDown className={cn(
                  "h-4 w-4 transition-transform duration-200",
                  accountMenuOpen && "rotate-180"
                )} />
              </Button>
              
              {/* Account Dropdown Menu */}
              {accountMenuOpen && (
                <div 
                  className="absolute right-0 top-full mt-2 w-56 bg-[#0f1724] border border-white/[0.08] rounded-lg shadow-xl overflow-hidden z-50"
                  role="menu"
                  aria-orientation="vertical"
                >
                  {/* User Info Header */}
                  <div className="px-4 py-3 border-b border-white/[0.06]">
                    <p className="text-xs text-[#E8E4DE]/50 uppercase tracking-wider">Signed in as</p>
                    <p className="text-sm text-[#E8E4DE] truncate mt-1">{user?.email}</p>
                  </div>
                  
                  {/* Menu Items */}
                  <div className="py-2">
                    <Link
                      to="/orders"
                      className="flex items-center gap-3 px-4 py-2.5 text-[14px] text-[#E8E4DE] hover:bg-white/[0.05] hover:text-[#C6A14A] transition-colors"
                      role="menuitem"
                      onClick={() => setAccountMenuOpen(false)}
                      data-testid="account-dropdown-orders"
                    >
                      <ShoppingBag className="h-4 w-4" />
                      Orders
                    </Link>
                    
                    {isAdmin && (
                      <Link
                        to="/admin"
                        className="flex items-center gap-3 px-4 py-2.5 text-[14px] text-[#C6A14A] hover:bg-[#C6A14A]/10 transition-colors"
                        role="menuitem"
                        onClick={() => setAccountMenuOpen(false)}
                        data-testid="account-dropdown-admin"
                      >
                        <Shield className="h-4 w-4" />
                        Dashboard
                      </Link>
                    )}

                    {isAdmin && (
                      <Link
                        to="/admin/clients"
                        className="flex items-center gap-3 px-4 py-2.5 text-[14px] text-[#E8E4DE] hover:bg-white/[0.05] hover:text-[#C6A14A] transition-colors"
                        role="menuitem"
                        onClick={() => setAccountMenuOpen(false)}
                        data-testid="account-dropdown-clients"
                      >
                        <Users className="h-4 w-4" />
                        Clients
                      </Link>
                    )}
                    
                    {isAdmin && (
                      <Link
                        to="/admin/real-estate-orders"
                        className="flex items-center gap-3 px-4 py-2.5 text-[14px] text-[#E8E4DE] hover:bg-white/[0.05] hover:text-[#C6A14A] transition-colors"
                        role="menuitem"
                        onClick={() => setAccountMenuOpen(false)}
                        data-testid="account-dropdown-real-estate-orders"
                      >
                        <Home className="h-4 w-4" />
                        Real Estate Orders
                      </Link>
                    )}
                    
                    <Link
                      to="/account"
                      className="flex items-center gap-3 px-4 py-2.5 text-[14px] text-[#E8E4DE] hover:bg-white/[0.05] hover:text-[#C6A14A] transition-colors"
                      role="menuitem"
                      onClick={() => setAccountMenuOpen(false)}
                      data-testid="account-dropdown-account"
                    >
                      <Settings className="h-4 w-4" />
                      Account Settings
                    </Link>
                  </div>
                  
                  {/* Sign Out */}
                  <div className="border-t border-white/[0.06] py-2">
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-3 w-full px-4 py-2.5 text-[14px] text-red-400 hover:bg-red-400/10 transition-colors"
                      role="menuitem"
                      data-testid="account-dropdown-signout"
                    >
                      <LogOut className="h-4 w-4" />
                      Sign Out
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <Button 
              asChild
              variant="ghost" 
              className={cn(
                "rounded-lg px-6 font-medium h-10 transition-all duration-300 text-[13px] tracking-[0.1em]",
                "bg-white/[0.08] text-[#E8E4DE] hover:bg-white/[0.12] hover:text-white border border-white/[0.06]"
              )}
              data-testid="navbar-signin-btn"
            >
              <Link to={loginTo}>Sign In</Link>
            </Button>
          )}
        </div>

        {/* Mobile Menu Button */}
        <button
          className={cn("xl:hidden p-2 transition-colors duration-200", textColorClass)}
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Toggle menu"
          data-testid="mobile-menu-btn"
        >
          {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Mobile Navigation - Luxury Editorial Style */}
      <div 
        className={cn(
          "xl:hidden fixed top-[72px] left-0 w-full bg-[#0a0e14] backdrop-blur-xl border-b border-white/[0.06] overflow-hidden transition-all duration-300 ease-out",
          isOpen ? "max-h-[calc(100dvh-72px)] opacity-100" : "max-h-0 opacity-0"
        )}
        style={{
          boxShadow: isOpen ? '0 25px 50px -12px rgba(0, 0, 0, 0.5)' : 'none'
        }}
      >
        <div className="p-6 pb-[calc(1.5rem+env(safe-area-inset-bottom,0px))] flex flex-col gap-2 max-h-[calc(100dvh-72px)] overflow-y-auto overscroll-contain">
          {/* Top actions: Book a Shoot, then Log In / My Account + Log Out (44px+ tap targets) */}
          <div className="flex flex-col gap-2 pb-4 mb-2 border-b border-white/[0.06]" data-testid="mobile-menu-top-actions">
            <Link
              to="/book"
              data-testid="mobile-menu-top-book"
              className="flex items-center justify-center min-h-[48px] rounded-lg bg-[#C6A14A] text-[#050816] hover:bg-[#D4AF5A] text-[13px] tracking-[0.1em] uppercase font-semibold transition-colors"
            >
              Book a Shoot
            </Link>
            {isAuthenticated ? (
              <div className="grid grid-cols-2 gap-2">
                <Link
                  to={isAdmin ? '/admin' : '/orders'}
                  data-testid="mobile-menu-top-account"
                  className="flex items-center justify-center gap-2 min-h-[48px] rounded-lg border border-[#C6A14A]/30 text-[#C6A14A] hover:bg-[#C6A14A]/10 text-[13px] tracking-[0.1em] uppercase font-medium transition-colors"
                >
                  <User className="h-4 w-4" />
                  My Account
                </Link>
                <button
                  type="button"
                  onClick={handleLogout}
                  data-testid="mobile-menu-top-logout"
                  className="flex items-center justify-center gap-2 min-h-[48px] rounded-lg border border-white/10 text-[#E8E4DE] hover:bg-white/5 text-[13px] tracking-[0.1em] uppercase font-medium transition-colors"
                >
                  <LogOut className="h-4 w-4" />
                  Log Out
                </button>
              </div>
            ) : (
              <Link
                to={loginTo}
                data-testid="mobile-menu-top-login"
                className="flex items-center justify-center gap-2 min-h-[48px] rounded-lg border border-[#C6A14A]/30 text-[#C6A14A] hover:bg-[#C6A14A]/10 text-[13px] tracking-[0.1em] uppercase font-medium transition-colors"
              >
                <User className="h-4 w-4" />
                Log In
              </Link>
            )}
          </div>

          {/* Home */}
          <Link 
            to="/" 
            className="text-[15px] font-medium py-3 px-4 text-[#E8E4DE] hover:text-[#C6A14A] hover:bg-white/[0.03] rounded-lg transition-all duration-200 tracking-[0.1em] uppercase"
          >
            Home
          </Link>

          {/* Explore - Expandable */}
          <div className="border-t border-white/[0.06] pt-2 mt-2">
            <button
              onClick={() => toggleMobileCategory('Explore')}
              className="w-full flex items-center justify-between text-[15px] font-medium py-3 px-4 text-[#E8E4DE] hover:text-[#C6A14A] hover:bg-white/[0.03] rounded-lg transition-all duration-200 tracking-[0.1em] uppercase"
            >
              <span>Explore</span>
              <ChevronDown className={cn(
                "h-4 w-4 transition-transform duration-300",
                expandedCategory === 'Explore' && "rotate-180"
              )} />
            </button>
            
            {/* Explore Submenu */}
            <div className={cn(
              "overflow-hidden transition-all duration-300 ease-out",
              expandedCategory === 'Explore' ? "max-h-[2000px] opacity-100" : "max-h-0 opacity-0"
            )}>
              <div className="pl-4 pb-4 space-y-4">
                {serviceGroups.map((group) => (
                  <div key={group.title} className="pt-4">
                    <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#C6A14A]/70 px-4 pb-2">
                      {group.title}
                    </h4>
                    <div className="space-y-1">
                      {group.items.map((service) => (
                        <Link
                          key={service.name}
                          to={service.href}
                          className="block py-2.5 px-6 text-[14px] text-[#E8E4DE]/60 hover:text-[#C6A14A] hover:bg-white/[0.02] rounded-lg transition-all duration-200"
                        >
                          {service.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Other Nav Items */}
          <Link 
            to="/pricing" 
            className="text-[15px] font-medium py-3 px-4 text-[#E8E4DE] hover:text-[#C6A14A] hover:bg-white/[0.03] rounded-lg transition-all duration-200 tracking-[0.1em] uppercase border-t border-white/[0.06] pt-5 mt-2"
          >
            Pricing
          </Link>
          <Link 
            to="/about" 
            className="text-[15px] font-medium py-3 px-4 text-[#E8E4DE] hover:text-[#C6A14A] hover:bg-white/[0.03] rounded-lg transition-all duration-200 tracking-[0.1em] uppercase"
          >
            About
          </Link>
          <Link 
            to="/contact" 
            className="text-[15px] font-medium py-3 px-4 text-[#E8E4DE] hover:text-[#C6A14A] hover:bg-white/[0.03] rounded-lg transition-all duration-200 tracking-[0.1em] uppercase"
          >
            Contact Us
          </Link>

          {/* Mobile Buttons */}
          <div className="flex flex-col gap-3 mt-6 pt-6 border-t border-white/[0.06]">
            {/* Phone Number with Dropdown - Mobile */}
            <div className="flex justify-center">
              <PhonePopover variant="navbar" data-testid="mobile-phone">
                <Phone className="w-4 h-4" />
                (928) 530-6604
              </PhonePopover>
            </div>
            
            {isAuthenticated ? (
              <>
                <Button 
                  asChild
                  className="w-full bg-white/[0.08] text-[#E8E4DE] hover:bg-white/[0.12] rounded-lg h-12 text-[13px] tracking-[0.1em] uppercase border border-white/[0.06]"
                >
                  <Link to="/orders">
                    <ShoppingBag className="mr-2 h-4 w-4" />
                    My Orders
                  </Link>
                </Button>
                {isAdmin && (
                  <Button 
                    asChild
                    className="w-full bg-[#C6A14A]/10 text-[#C6A14A] hover:bg-[#C6A14A]/20 rounded-lg h-12 text-[13px] tracking-[0.1em] uppercase border border-[#C6A14A]/20"
                  >
                    <Link to="/admin" data-testid="mobile-menu-dashboard">
                      <Shield className="mr-2 h-4 w-4" />
                      Dashboard
                    </Link>
                  </Button>
                )}
                {isAdmin && (
                  <Button 
                    asChild
                    className="w-full bg-white/[0.08] text-[#E8E4DE] hover:bg-white/[0.12] rounded-lg h-12 text-[13px] tracking-[0.1em] uppercase border border-white/[0.06]"
                  >
                    <Link to="/admin/clients" data-testid="mobile-menu-clients">
                      <Users className="mr-2 h-4 w-4" />
                      Clients
                    </Link>
                  </Button>
                )}
                {isAdmin && (
                  <Button 
                    asChild
                    className="w-full bg-white/[0.08] text-[#E8E4DE] hover:bg-white/[0.12] rounded-lg h-12 text-[13px] tracking-[0.1em] uppercase border border-white/[0.06]"
                  >
                    <Link to="/admin/real-estate-orders">
                      <Home className="mr-2 h-4 w-4" />
                      Real Estate Orders
                    </Link>
                  </Button>
                )}
                <Button 
                  asChild
                  className="w-full bg-white/[0.08] text-[#E8E4DE] hover:bg-white/[0.12] rounded-lg h-12 text-[13px] tracking-[0.1em] uppercase border border-white/[0.06]"
                >
                  <Link to="/account">
                    <Settings className="mr-2 h-4 w-4" />
                    Account
                  </Link>
                </Button>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </header>
  );
};

const ListItem = React.forwardRef(({ className, title, children, ...props }, ref) => {
  return (
    <li>
      <NavigationMenuLink asChild>
        <a
          ref={ref}
          className={cn(
            "block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent/10 hover:text-accent-foreground focus:bg-accent/10 focus:text-accent-foreground",
            className
          )}
          {...props}
        >
          <div className="text-sm font-medium leading-none text-black">{title}</div>
          {children && (
            <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
              {children}
            </p>
          )}
        </a>
      </NavigationMenuLink>
    </li>
  )
})
ListItem.displayName = "ListItem"

export default Navbar;
